import { Dialog, DialogTitle, DialogContent, DialogActions, IconContainerProps, Rating, styled, FormControl, FormHelperText } from "@mui/material";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { HabilidadeDropDown } from "../../../../domain/models/habilidade_dropdown";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { HabilidadeItemForm, HabilidadeItemSchema } from "../../../schemas/Funcionarios_cadastro/habilidade_item_schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { HabilidadeItem } from "../../../../domain/models/habilidade_item";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';

/**
 * Icons
 */

const customIcons: {
  [index: string]: {
    textColor: string;
    icon: React.ReactElement<unknown>;
    label: string;
  };
} = {
  1: {
    textColor: 'text-red-500',
    icon: <SentimentVeryDissatisfiedIcon className="text-red-500" />,
    label: 'Very Dissatisfied',
  },
  2: {
    textColor: 'text-orange-500',
    icon: <SentimentDissatisfiedIcon className="text-orange-500" />,
    label: 'Dissatisfied',
  },
  3: {
    textColor: 'text-yellow-500',
    icon: <SentimentSatisfiedIcon className="text-yellow-500" />,
    label: 'Neutral',
  },
  4: {
    textColor: 'text-green-300',
    icon: <SentimentSatisfiedAltIcon className="text-green-300" />,
    label: 'Satisfied',
  },
  5: {
    textColor: 'text-green-600',
    icon: <SentimentVerySatisfiedIcon className="text-green-600" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  const isSelected = other.className?.includes('MuiRating-iconFilled');
  return (
    <span {...other}>
      <div className="flex flex-col items-center">
        <span className="flex">
          {customIcons[value].icon}
        </span>
        <span className={`${isSelected ? customIcons[value].textColor : ''} text-xs`}>
          {value}
        </span>
      </div >
    </span >
  );
}

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

/**
 * CadastroHabilidadeItemModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedHabilidadeItem: HabilidadeItem | undefined,
  habilidades: HabilidadeDropDown[],
  onSubmit: (form: HabilidadeItemForm) => void,
  onClose: () => void,
};

export const CadastroHabilidadeItemModal = ({
  open,
  saving,
  habilidades,
  selectedHabilidadeItem,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<HabilidadeItemForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(HabilidadeItemSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedHabilidadeItem) {
        hookForm.setValue('idHabilidade', selectedHabilidadeItem.idHabilidade);
        hookForm.setValue('nota', selectedHabilidadeItem.nota ?? 0);
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedHabilidadeItem,
  ]);

  const handleSubmit = useCallback((form: HabilidadeItemForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "40rem",
          },
        },
      }}>
      <DialogTitle>
        {selectedHabilidadeItem ? "Editar" : "Cadastrar"} item de habilidade
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Avaliação"} arrangement="fullRow">
            <>
              <DefaultFormAutocomplete
                name='idHabilidade'
                label='Habilidade'
                required={true}
                readOnly={saving}
                options={habilidades}
                getOptionLabel={(opt: HabilidadeDropDown) => opt.tipo}
                getOptionKey={(opt: HabilidadeDropDown) => opt.idHabilidade}
                reactHookForm={hookForm} />

              <Controller
                name='nota'
                control={hookForm.control}
                render={(({ field }) => (
                  <FormControl>
                    <StyledRating
                      className="w-full h-full"
                      readOnly={saving}
                      getLabelText={(value: number) => customIcons[value].label}
                      highlightSelectedOnly={true}
                      value={field.value ?? null}
                      onChange={(_, value) => {
                        hookForm.clearErrors('nota');
                        hookForm.setValue('nota', value ?? 1);
                      }}
                      slotProps={{
                        root: { className: "flex flex-row justify-center items-center gap-2", },
                        icon: { component: IconContainer, }
                      }}
                    />

                    {hookForm.formState.errors.nota &&
                      <FormHelperText sx={{ paddingX: 0 }} error={true}>
                        {hookForm.formState.errors.nota.message}
                      </FormHelperText>
                    }

                  </FormControl>
                ))}
              />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}